/**
 * Main's PatientContacts envelope (keyon `patient_contacts`, #260) on the
 * port's `/patients/{patient}/contacts` — ACC-16, FEM-46.
 *
 * Main's contact screens (UserContacts, UserContact, AddUserForm, the
 * Welcome page's primary-contact card) post `PatientContacts.<Meth>` through
 * `src/api/patientContactsApi.js` and read main's row back: camelCase fields
 * keyed by `id`. This module is that translation in both directions, in one
 * place, used by the four bridge entries in bridgeMap.js.
 *
 * Three rules main did not keep, and the port does:
 *
 *  - **The chart is the patient the call names**, never the open chart in its
 *    place: AddUserForm writes a contact for the patient picked in the form,
 *    which is not the chart on screen. A call naming no patient addresses
 *    nothing.
 *  - **A contact is addressed on its own chart.** Main's UPDATE was keyed by
 *    `contact_uuid` alone and rewrote the account and patient it belonged to,
 *    so knowing a uuid moved a contact onto another tenant's chart. Here the
 *    path names the chart, the API refuses a contact that is not on it, and no
 *    chart, login or tenant field is ever sent in a body.
 *  - **No SSN and no invitation token cross this boundary.** Main returned
 *    both from GetPatientContacts; the port stores neither
 *    (pacmny-api 2026_10_15_000300), and `ssn` reads back empty.
 *
 * Main's `id` was a client-minted uuid. Here a contact read from the API is
 * addressed by the API's id; a new contact's uuid is sent as
 * `client_reference`, which the API uses to recognise a retried save of the
 * same new contact rather than adding the person twice.
 */

import api, { ApiError } from './client';
import { RELATION_DESCRIPTIONS } from '@/data/referenceLists';

/** The API's relationship members, which the port's relation list offers by id. */
const RELATIONSHIPS = new Set(RELATION_DESCRIPTIONS.map((r) => r.id));

/** Main's numbered phone types (1 CELL, 2 HOME, 3 WORK) and the port's names. */
const PHONE_TYPE_BY_NUMBER = { 1: 'mobile', 2: 'home', 3: 'work' };
const PHONE_TYPES = new Set(['mobile', 'home', 'work', 'other']);

/** Main's form records the Nursys status in capitals; the API stores the word. */
const LICENSE_STATUSES = new Set(['unencumbered', 'encumbered', 'expired']);

const blank = (v) => v === null || v === undefined || String(v).trim() === '';
const text = (v) => (blank(v) ? null : String(v).trim());

/** The chart a PatientContacts call names, or null: never the open chart in its place. */
export function namedPatientId(p) {
  const raw = p?.patient_unique_id ?? p?.patientUniqueId ?? p?.patientId;
  const n = Number(raw);

  return Number.isInteger(n) && n > 0 ? n : null;
}

/** The API id a contact read back carries; a client uuid is not one. */
export function contactApiId(p) {
  const raw = p?.db_id ?? p?.contact_uuid ?? p?.id;

  return /^\d+$/.test(String(raw ?? '')) ? Number(raw) : null;
}

function phoneType(v) {
  if (blank(v)) return null;
  if (PHONE_TYPES.has(String(v))) return String(v);

  return PHONE_TYPE_BY_NUMBER[Number(v)] || null;
}

/** A two-letter state: the port's lists use the code as the id. */
function stateCode(code, abbr) {
  const candidate = text(abbr) || text(code);

  return candidate && /^[A-Za-z]{2}$/.test(candidate) ? candidate.toUpperCase() : null;
}

function relationshipOf(p) {
  const type = text(p.relationType ?? p.relation_type);
  const label = text(p.relationName ?? p.relation_name);

  if (type && RELATIONSHIPS.has(type) && type !== 'other') {
    return { relationship: type, relationship_other: null };
  }

  // `other`, main's numbered relation list, or a label with no member here:
  // kept as its own words rather than forced into the nearest member. The API
  // needs words for `other`; with none given they are the option's own.
  return { relationship: 'other', relationship_other: label || 'Other' };
}

function isoOrNull(v) {
  if (blank(v)) return null;
  const d = new Date(v);

  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/**
 * Main's contact form -> the API's body. An allowlist: every field main's
 * SavePatientContact stored that the port keeps, and nothing that names a
 * chart, a login, a tenant or an SSN.
 */
export function contactBody(p) {
  const licenseStatus = text(p.status ?? p.license_status);
  const license = text(p.license ?? p.license_number);

  return {
    first_name: text(p.firstName ?? p.first_name) ?? text(p.lastName ?? p.last_name),
    last_name: text(p.firstName ?? p.first_name) ? text(p.lastName ?? p.last_name) : null,
    ...relationshipOf(p),
    email: text(p.email),
    phone: text(p.primaryPhone ?? p.primary_phone),
    phone_type: phoneType(p.primaryPhoneType ?? p.primary_phone_type),
    secondary_phone: text(p.secondaryPhone ?? p.secondary_phone),
    secondary_phone_type: phoneType(p.secondaryPhoneType ?? p.secondary_phone_type),
    address_line1: text(p.address1),
    address_line2: text(p.address2),
    city: text(p.city),
    state: stateCode(p.state, p.stateAbbr),
    postal_code: text(p.zip),
    mailing_address_line1: text(p.secondaryAddress1),
    mailing_address_line2: text(p.secondaryAddress2),
    mailing_city: text(p.secondaryCity),
    mailing_state: stateCode(p.secondaryState, p.secondaryStateAbbr),
    mailing_postal_code: text(p.secondaryZip),
    date_of_birth: text(p.dob) ? String(p.dob).slice(0, 10) : null,
    license_number: license,
    license_state: license ? stateCode(p.licenseState, p.licenseStateAbbr) : null,
    license_status: licenseStatus && LICENSE_STATUSES.has(licenseStatus.toLowerCase()) ? licenseStatus.toLowerCase() : null,
    license_status_checked_at: licenseStatus ? isoOrNull(p.status_time) : null,
    notes: text(p.notes),
    is_primary: bool(p.primaryContact ?? p.primary_contact),
  };
}

function bool(v) {
  return v === true || v === 1 || v === '1' || v === 'true';
}

/** What main's GetPatientContacts read back for the linked login's invitation. */
function invitationStatus(login) {
  if (!login) return 'not_invited';

  switch (login.status) {
    case 'active':
      return 'accepted';
    case 'expired':
      return 'expired';
    case 'invited':
    case 'pending_verification':
      return 'invited';
    default:
      return login.status || 'not_invited';
  }
}

/** The API's contact -> main's row (SqlClass::GetPatientContacts). */
export function contactRow(contact, patientId) {
  const label = contact.relationship === 'other'
    ? (contact.relationship_other || 'Other')
    : (RELATION_DESCRIPTIONS.find((r) => r.id === contact.relationship)?.description || contact.relationship || '');

  return {
    id: String(contact.id),
    contact_uuid: String(contact.id),
    db_id: contact.id,
    client_reference: contact.client_reference || null,
    patient_unique_id: String(patientId),
    patientUniqueId: String(patientId),
    firstName: contact.first_name || '',
    lastName: contact.last_name || '',
    dob: contact.date_of_birth || '',
    gender: contact.gender || '',
    // Not stored by the port; empty, as main's form expects the key.
    ssn: '',
    relationType: contact.relationship || '',
    relationName: label,
    primaryContact: Boolean(contact.is_primary),
    email: contact.email || '',
    primaryPhone: contact.phone || '',
    primaryPhoneType: contact.phone_type || '',
    secondaryPhone: contact.secondary_phone || '',
    secondaryPhoneType: contact.secondary_phone_type || '',
    address1: contact.address_line1 || '',
    address2: contact.address_line2 || '',
    city: contact.city || '',
    state: contact.state || '',
    stateAbbr: contact.state || '',
    zip: contact.postal_code || '',
    secondaryAddress1: contact.mailing_address_line1 || '',
    secondaryAddress2: contact.mailing_address_line2 || '',
    secondaryCity: contact.mailing_city || '',
    secondaryState: contact.mailing_state || '',
    secondaryStateAbbr: contact.mailing_state || '',
    secondaryZip: contact.mailing_postal_code || '',
    license: contact.license_number || '',
    licenseState: contact.license_state || '',
    licenseStateAbbr: contact.license_state || '',
    licenseId: 0,
    status: contact.license_status ? String(contact.license_status).toUpperCase() : '',
    status_time: contact.license_status_checked_at || null,
    registered_unique_id: contact.login ? contact.login.user_id : (contact.user_id ? String(contact.user_id) : null),
    invitation_status: invitationStatus(contact.login),
    invite_sent_at: contact.login ? contact.login.invited_at : null,
    notes: contact.notes || '',
  };
}

/** Main's error body, which patientContactsApi reads its message out of. */
function refusal(error) {
  if (!(error instanceof ApiError)) throw error;

  return { status: 'Error', code: error.code, message: error.message, errors: error.fieldErrors };
}

function noChart() {
  return { status: 'Error', code: 'patient_required', message: 'Missing patient scope (account number or patient ID)' };
}

/** PatientContacts.GetPatientContacts: every contact, primary first, as main ordered them. */
export async function getPatientContacts(p) {
  const patientId = namedPatientId(p);

  if (patientId === null) return { status: 'Successful', data: [] };

  try {
    const contacts = [];

    // Bounded: twenty pages of a hundred is far past any chart's contacts.
    for (let page = 1; page <= 20; page += 1) {
      // eslint-disable-next-line no-await-in-loop
      const result = await api.list(`/patients/${patientId}/contacts`, { page, per_page: 100 });
      contacts.push(...(result.items || []));

      const last = result.meta && Number(result.meta.last_page);
      if (!last || page >= last) break;
    }

    const rows = contacts
      .map((contact) => contactRow(contact, patientId))
      .sort((a, b) => (Number(b.primaryContact) - Number(a.primaryContact)) || (a.db_id - b.db_id));

    return { status: 'Successful', data: rows };
  } catch (error) {
    return refusal(error);
  }
}

/**
 * PatientContacts.SavePatientContact (and Create/Update): an update when the
 * call names a contact the API gave out, otherwise a create carrying main's
 * uuid as `client_reference`. `registered_unique_id` is not sent: main let any
 * caller name the login a contact row pointed at, and in the port that link IS
 * chart access, so it is made only by the contact-account route
 * (`provisionContactLogin`), which checks who may make it.
 */
export async function savePatientContact(p) {
  const patientId = namedPatientId(p);

  if (patientId === null) return noChart();

  const contactId = contactApiId(p);
  const body = contactBody(p);

  try {
    let contact;

    if (contactId !== null) {
      contact = await api.patch(`/patients/${patientId}/contacts/${contactId}`, body);
    } else {
      const reference = text(p.contact_uuid ?? p.id);
      contact = await api.post(`/patients/${patientId}/contacts`, { ...body, client_reference: reference || undefined });
    }

    return {
      status: 'Successful',
      contact_uuid: String(contact.id),
      id: String(contact.id),
      primary_contact: contact.is_primary ? 1 : 0,
    };
  } catch (error) {
    return refusal(error);
  }
}

/** PatientContacts.DeletePatientContact. */
export async function deletePatientContact(p) {
  const patientId = namedPatientId(p);
  const contactId = contactApiId(p);

  if (patientId === null) return noChart();
  if (contactId === null) return { status: 'Error', code: 'not_found', message: 'Missing contact identifier' };

  try {
    await api.delete(`/patients/${patientId}/contacts/${contactId}`);

    return { status: 'Successful', deleted_uuid: String(contactId) };
  } catch (error) {
    return refusal(error);
  }
}

/** PatientContacts.SetPrimaryContact: set (PUT .../primary) or clear (PATCH is_primary false). */
export async function setPrimaryContact(p) {
  const patientId = namedPatientId(p);
  const contactId = contactApiId(p);

  if (patientId === null) return noChart();
  if (contactId === null) return { status: 'Error', code: 'not_found', message: 'Missing contact identifier' };

  const primary = bool(p.primary_contact ?? p.primaryContact);

  try {
    if (primary) {
      await api.put(`/patients/${patientId}/contacts/${contactId}/primary`);
    } else {
      await api.patch(`/patients/${patientId}/contacts/${contactId}`, { is_primary: false });
    }

    return { status: 'Successful', contact_uuid: String(contactId), primary_contact: primary ? 1 : 0 };
  } catch (error) {
    return refusal(error);
  }
}

/**
 * A login for a contact (ACC-19): main's Add User → Contacts and the Contacts
 * page's Register button. The contact is created (or, from Register, already
 * on the chart) and then given its login, which joins the patient's Contacts
 * group; pacmny-auth mails the invitation. Resolves the API's answer.
 *
 * `linkExisting`: the operator confirmed that the contact login the address
 * already belongs to is this person (the API answered 409
 * `contact_login_exists`, naming it). Never sent unasked: an existing login is
 * linked only on that confirmation, and never on a patient's own request.
 */
export async function provisionContactLogin(patientId, contactId, { linkExisting = false } = {}) {
  const path = `/patients/${patientId}/contacts/${contactId}/account`;

  return linkExisting ? api.post(path, { link_existing: true }) : api.post(path);
}

export default {
  getPatientContacts,
  savePatientContact,
  deletePatientContact,
  setPrimaryContact,
  provisionContactLogin,
  contactBody,
  contactRow,
};
